import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Settings,
  User,
  LogOut,
  Bell,
  Shield,
  ExternalLink,
  ChevronRight,
  Mail,
  Sparkles,
  LayoutDashboard,
  BookOpen,
  AlertCircle,
  CheckCircle2,
  Lock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getAuthMessage } from '../utils/authErrors';
import { NotificationType } from '../types';
import {
  getMergedNotificationPreferences,
  saveNotificationPreferences,
} from '../api/userSettings';

const ProfileSettings: React.FC = () => {
  const { currentUser, logout, emailVerified, sendVerificationEmail, updateUserPassword } = useAuth();
  const navigate = useNavigate();

  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);

  const [notifPrefs, setNotifPrefs] = useState<Record<NotificationType, boolean> | null>(null);
  const [notifPrefsLoading, setNotifPrefsLoading] = useState(true);
  const [notifSavingKey, setNotifSavingKey] = useState<NotificationType | null>(null);
  const [notifError, setNotifError] = useState<string | null>(null);

  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<string | null>(null);
  const [passwordErr, setPasswordErr] = useState<string | null>(null);

  const NOTIFICATION_ROWS: { key: NotificationType; label: string; sub: string }[] = [
    { key: 'connection_request', label: 'Connection requests', sub: 'When a tasker asks to connect on your task' },
    { key: 'connection_accepted', label: 'Request accepted', sub: 'When your request to connect is accepted' },
    { key: 'connection_rejected', label: 'Request declined', sub: 'When your request is declined' },
    { key: 'new_message', label: 'Chat messages', sub: 'New messages in task chats' },
    { key: 'task_update', label: 'Task updates', sub: 'Changes and activity on your tasks' },
    { key: 'task_assigned', label: 'Assignments', sub: 'When work is assigned to you' },
    { key: 'task_completed', label: 'Completions', sub: 'When a task is marked done' },
    { key: 'system', label: 'System', sub: 'Important platform notices' },
  ];

  const loadNotifPrefs = useCallback(async () => {
    if (!currentUser) return;
    setNotifPrefsLoading(true);
    setNotifError(null);
    try {
      const merged = await getMergedNotificationPreferences(currentUser.uid);
      setNotifPrefs(merged);
    } catch (e) {
      setNotifError(e instanceof Error ? e.message : 'Could not load preferences');
    } finally {
      setNotifPrefsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    void loadNotifPrefs();
  }, [loadNotifPrefs]);

  const handleNotifToggle = async (key: NotificationType) => {
    if (!currentUser || !notifPrefs) return;
    const next = !notifPrefs[key];
    setNotifSavingKey(key);
    setNotifError(null);
    setNotifPrefs((prev) => (prev ? { ...prev, [key]: next } : prev));
    try {
      await saveNotificationPreferences(currentUser.uid, { [key]: next });
    } catch (e) {
      setNotifPrefs((prev) => (prev ? { ...prev, [key]: !next } : prev));
      setNotifError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setNotifSavingKey(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleResendVerification = async () => {
    if (!currentUser) return;
    setVerifyMessage(null);
    setVerifyLoading(true);
    try {
      await sendVerificationEmail();
      setVerifyMessage('Verification email sent. Check your inbox.');
    } catch (e: unknown) {
      setVerifyMessage(e instanceof Error ? e.message : 'Could not send email.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordErr(null);
    setPasswordMsg(null);
    if (newPassword.length < 6) {
      setPasswordErr('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordErr('Passwords do not match.');
      return;
    }
    setPasswordBusy(true);
    try {
      await updateUserPassword(newPassword);
      setNewPassword('');
      setConfirmNewPassword('');
      setPasswordMsg('Password updated successfully.');
    } catch (e: unknown) {
      setPasswordErr(getAuthMessage(e));
    } finally {
      setPasswordBusy(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-tech-dark bg-tech-mesh flex items-center justify-center px-4">
        <div className="tech-card max-w-md w-full rounded-2xl p-8 text-center">
          <Settings className="w-12 h-12 text-tech-cyan mx-auto mb-4" aria-hidden />
          <h1 className="text-xl font-bold text-tech-text mb-2">Sign in required</h1>
          <p className="text-tech-text-muted text-sm mb-6">Open settings after you log in.</p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-5 py-3 rounded-xl font-semibold text-tech-darker bg-tech-primary"
          >
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  const displayName = currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
  const publicProfileUrl = `${window.location.origin}/tasker/${currentUser.uid}`;

  // Detect provider from Supabase raw user identities
  const identities = (currentUser.raw?.identities ?? []) as Array<{ provider: string }>;
  const hasGoogle = identities.some((i) => i.provider === 'google');
  const hasPassword = identities.some((i) => i.provider === 'email');

  const sections: Array<{
    title: string;
    description: string;
    items: Array<{
      label: string;
      sub?: string;
      to?: string;
      href?: string;
      external?: boolean;
      icon: React.ReactNode;
      onClick?: () => void;
    }>;
  }> = [
    {
      title: 'Your AI profile',
      description:
        'Build a public profile that showcases AI tools you use, skills, and public tasks — your one-stop presence for people who ship with AI.',
      items: [
        {
          label: 'Edit public profile & skills',
          sub: 'Bio, specialties, portfolio, availability — shareable like a link.',
          to: '/tasker-meta',
          icon: <Sparkles className="w-5 h-5 text-tech-accent" />,
        },
        {
          label: 'Preview public profile',
          sub: 'See what others see when you share your link.',
          href: publicProfileUrl,
          external: true,
          icon: <ExternalLink className="w-5 h-5 text-tech-cyan" />,
        },
        {
          label: 'Private profile overview',
          sub: 'Completion status and quick stats.',
          to: '/tasker-profile',
          icon: <User className="w-5 h-5 text-tech-text-muted" />,
        },
      ],
    },
    {
      title: 'Account & security',
      description: 'Email, verification, and password.',
      items: [
        {
          label: currentUser.email || 'Email',
          sub: emailVerified ? 'Verified' : 'Not verified — verify to unlock full trust on the platform.',
          icon: <Mail className="w-5 h-5 text-tech-cyan" />,
        },
        ...(!emailVerified
          ? [
              {
                label: verifyLoading ? 'Sending…' : 'Send verification email',
                sub: "We'll send a link to your inbox.",
                onClick: handleResendVerification,
                icon: <AlertCircle className="w-5 h-5 text-amber-400" />,
              } as const,
            ]
          : []),
        {
          label: 'Reset password',
          sub: 'Send yourself a password-reset email.',
          to: '/forgot-password',
          icon: <Shield className="w-5 h-5 text-tech-text-muted" />,
        },
      ],
    },
    {
      title: 'Activity',
      description: 'Tasks, guides, and alerts.',
      items: [
        {
          label: 'Task dashboard',
          to: '/task-dashboard',
          icon: <LayoutDashboard className="w-5 h-5 text-tech-cyan" />,
        },
        {
          label: 'Create a task',
          to: '/task-hatch',
          icon: <Sparkles className="w-5 h-5 text-tech-accent" />,
        },
        {
          label: 'Implementation guides',
          to: '/implementation-guides',
          icon: <BookOpen className="w-5 h-5 text-tech-text-muted" />,
        },
        {
          label: 'Notifications',
          to: '/notifications',
          icon: <Bell className="w-5 h-5 text-tech-text-muted" />,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-tech-dark text-tech-text bg-tech-mesh">
      <div className="max-w-3xl mx-auto px-4 py-10 md:py-14">
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 rounded-xl bg-tech-cyan/10 border border-tech-cyan/20">
              <Settings className="w-7 h-7 text-tech-cyan" aria-hidden />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Profile &amp; settings</h1>
              <p className="text-tech-text-muted text-sm mt-1">
                Manage your account, public AI profile, and how you show up on GNK Continuum.
              </p>
            </div>
          </div>

          <div className="tech-card rounded-2xl p-5 md:p-6 mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-tech-darker shrink-0"
                style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
              >
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-tech-text truncate">{displayName}</p>
                <p className="text-sm text-tech-text-muted truncate">{currentUser.email}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  {emailVerified ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-tech-accent shrink-0" aria-hidden />
                      <span className="text-xs text-tech-accent">Email verified</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" aria-hidden />
                      <span className="text-xs text-amber-200/90">Email not verified</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="sm:ml-auto flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-tech-border text-tech-text-muted hover:text-tech-cyan hover:border-tech-cyan/40 hover:bg-tech-gray/50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Log out
              </button>
            </div>
          </div>

          {verifyMessage && (
            <p
              className={`mt-4 text-sm px-4 py-3 rounded-xl ${
                verifyMessage.includes('sent') || verifyMessage.includes('Verification')
                  ? 'bg-tech-accent/15 text-tech-accent border border-tech-accent/30'
                  : 'bg-amber-500/15 text-amber-200 border border-amber-500/30'
              }`}
            >
              {verifyMessage}
            </p>
          )}
        </header>

        <div className="space-y-10">
          {/* Sign-in methods */}
          <section>
            <h2 className="text-lg font-semibold text-tech-text mb-1">Sign-in methods</h2>
            <p className="text-sm text-tech-text-muted mb-4 max-w-2xl">
              Your active sign-in providers for this account.
            </p>
            <div className="tech-card rounded-2xl p-5 md:p-6 border border-tech-border/80 space-y-4">
              <div className="flex flex-wrap gap-2">
                {hasGoogle && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-tech-cyan/40 bg-tech-cyan/10 text-tech-cyan">
                    Google · linked
                  </span>
                )}
                {hasPassword && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-tech-accent/40 bg-tech-accent/10 text-tech-accent">
                    Email &amp; password · linked
                  </span>
                )}
              </div>

              {/* Change password */}
              {hasPassword && (
                <form onSubmit={handleChangePassword} className="space-y-3 max-w-md pt-2">
                  <p className="text-sm text-tech-text-muted font-medium">Change password</p>
                  {passwordErr && (
                    <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">{passwordErr}</p>
                  )}
                  {passwordMsg && (
                    <p className="text-sm text-tech-accent bg-tech-accent/10 border border-tech-accent/25 rounded-lg px-3 py-2">{passwordMsg}</p>
                  )}
                  <div>
                    <label htmlFor="settings-new-pw" className="block text-xs font-medium text-tech-text-muted mb-1">
                      New password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tech-text-muted" aria-hidden />
                      <input
                        id="settings-new-pw"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                        className="w-full pl-9 pr-4 rounded-xl bg-tech-dark border border-tech-border px-3 py-2.5 text-sm text-tech-text focus:outline-none focus:ring-2 focus:ring-tech-cyan/40"
                        placeholder="At least 6 characters"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="settings-confirm-pw" className="block text-xs font-medium text-tech-text-muted mb-1">
                      Confirm password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tech-text-muted" aria-hidden />
                      <input
                        id="settings-confirm-pw"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        autoComplete="new-password"
                        className="w-full pl-9 pr-4 rounded-xl bg-tech-dark border border-tech-border px-3 py-2.5 text-sm text-tech-text focus:outline-none focus:ring-2 focus:ring-tech-cyan/40"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={passwordBusy}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-tech-darker disabled:opacity-50 text-sm"
                    style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
                  >
                    {passwordBusy ? 'Saving…' : 'Update password'}
                  </button>
                </form>
              )}
            </div>
          </section>

          {/* Notification preferences */}
          <section>
            <h2 className="text-lg font-semibold text-tech-text mb-1">Notifications</h2>
            <p className="text-sm text-tech-text-muted mb-4 max-w-2xl">
              Choose which in-app alerts we create for you. Turning a category off stops new notifications of that
              type — existing ones stay in your list until you clear them.
            </p>
            <div className="tech-card rounded-2xl overflow-hidden border border-tech-border/80">
              {notifPrefsLoading && (
                <div className="px-4 py-8 text-center text-sm text-tech-text-muted">Loading preferences…</div>
              )}
              {notifError && (
                <div className="px-4 py-3 text-sm text-amber-200 bg-amber-500/10 border-b border-amber-500/20">
                  {notifError}
                </div>
              )}
              {!notifPrefsLoading && notifPrefs && (
                <ul className="divide-y divide-tech-border/60">
                  {NOTIFICATION_ROWS.map((row) => (
                    <li key={row.key} className="flex items-center justify-between gap-4 px-4 py-4 hover:bg-tech-gray/30 transition-colors">
                      <div className="min-w-0">
                        <p className="font-medium text-tech-text">{row.label}</p>
                        <p className="text-sm text-tech-text-muted mt-0.5">{row.sub}</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={notifPrefs[row.key]}
                        disabled={notifSavingKey === row.key}
                        onClick={() => void handleNotifToggle(row.key)}
                        className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-tech-cyan/50 disabled:opacity-60 ${
                          notifPrefs[row.key] ? 'bg-tech-accent' : 'bg-tech-gray'
                        }`}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                            notifPrefs[row.key] ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-lg font-semibold text-tech-text mb-1">{section.title}</h2>
              <p className="text-sm text-tech-text-muted mb-4 max-w-2xl">{section.description}</p>
              <div className="tech-card rounded-2xl divide-y divide-tech-border/60 overflow-hidden">
                {section.items.map((item, idx) => {
                  const content = (
                    <>
                      <span className="p-2 rounded-lg bg-tech-gray/50 shrink-0">{item.icon}</span>
                      <div className="min-w-0 flex-1 text-left">
                        <p className="font-medium text-tech-text">{item.label}</p>
                        {item.sub && <p className="text-sm text-tech-text-muted mt-0.5">{item.sub}</p>}
                      </div>
                      {(item.to || item.href || item.onClick) && (
                        <ChevronRight className="w-5 h-5 text-tech-text-muted shrink-0" aria-hidden />
                      )}
                    </>
                  );

                  if (item.href) {
                    return (
                      <a
                        key={`${section.title}-${idx}`}
                        href={item.href}
                        target={item.external ? '_blank' : undefined}
                        rel={item.external ? 'noopener noreferrer' : undefined}
                        className="flex items-center gap-4 px-4 py-4 hover:bg-tech-gray/40 transition-colors"
                      >
                        {content}
                      </a>
                    );
                  }
                  if (item.to) {
                    return (
                      <Link
                        key={`${section.title}-${idx}`}
                        to={item.to}
                        className="flex items-center gap-4 px-4 py-4 hover:bg-tech-gray/40 transition-colors"
                      >
                        {content}
                      </Link>
                    );
                  }
                  if (item.onClick) {
                    return (
                      <button
                        key={`${section.title}-${idx}`}
                        type="button"
                        onClick={item.onClick}
                        disabled={verifyLoading && item.label.includes('Send')}
                        className="flex items-center gap-4 w-full px-4 py-4 hover:bg-tech-gray/40 transition-colors text-left disabled:opacity-60"
                      >
                        {content}
                      </button>
                    );
                  }
                  return (
                    <div
                      key={`${section.title}-${idx}`}
                      className="flex items-center gap-4 px-4 py-4"
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          <section>
            <h2 className="text-lg font-semibold text-tech-text mb-4">Session</h2>
            <button
              type="button"
              onClick={handleLogout}
              className="w-full tech-card rounded-2xl px-5 py-4 flex items-center justify-center gap-2 font-semibold text-tech-text border border-red-500/30 hover:bg-red-500/10 hover:border-red-500/50 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Log out of GNK Continuum
            </button>
            <p className="text-xs text-tech-text-muted mt-3 text-center">
              Share your public profile:{' '}
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(publicProfileUrl);
                }}
                className="text-tech-cyan hover:underline"
              >
                Copy link
              </button>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
